import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { User, TrendingUp, Award, BarChart2, Target, MessageCircle, Instagram, Twitter, ChevronDown, ChevronUp, AlertTriangle, ThumbsUp, ThumbsDown, Activity, Youtube, Facebook, Linkedin, PlusCircle, XCircle, Play, FilePlus, CheckCircle, Info, Trash2, ArrowUpRight, Video, ArrowLeft } from 'lucide-react';
import { useNavigate , useParams} from 'react-router-dom';
import PlayerChatbot from './PlayerChatbot';
import { samplePlayers } from '../PlayerRoster';




const PlayerProfile = () => {
  const navigate = useNavigate();
  const { playerId } = useParams();
  console.log('playerId==>',playerId)
  // State for collapsible sections and modals
  const [socialMediaExpanded, setSocialMediaExpanded] = useState(false);
  const [animateMetrics, setAnimateMetrics] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  // const [playerData , setPlayerData ] = useState();
  const playerData = samplePlayers?.filter(p=>p.personal_info.name.toLowerCase().replace(/\s+/g, '-') == playerId)?.[0]
  console.log('playerData==>',playerData)  
  // Position-specific tabs - default to first tab based on player position
  const [positionTab, setPositionTab] = useState('');
  
  // Active tab for performance data
  const [activeTab, setActiveTab] = useState('athletic-performance');
  
  useEffect(() => {
    // Determine initial position tab based on player position
    if (playerData?.personal_info?.position === "Pitcher") {
      setPositionTab('pitching-metrics');
    } else {
      setPositionTab('offensive-stats');
    }
    
    // Trigger metric animation after component mounts
    const timer = setTimeout(() => {
      setAnimateMetrics(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [playerData]);

  // Toggle social media section
  const toggleSocialMedia = () => {
    setSocialMediaExpanded(!socialMediaExpanded);
  };
  
  // Tab component for performance metrics
  const PerformanceTab = ({ id, label, active, onClick }) => (
    <button
      className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
        active
          ? 'bg-[#0A0A0A] border-t border-l border-r border-[#1A1A1A] text-[#E8B800]'
          : 'bg-[#111111] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]'
      }`}
      onClick={() => onClick(id)}
    >
      {label}
    </button>
  );
  
  // Tab component for position-specific data
  const PositionTab = ({ id, label, active, onClick }) => (
    <button
      className={`px-3 py-1 text-xs font-medium rounded-lg ${
        active
          ? 'bg-[#E8B800] text-black'
          : 'bg-[#111111] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]'
      }`}
      onClick={() => onClick(id)}
    >
      {label}
    </button>
  );

  // Format player stats based on position
  const getPositionTabs = () => {
    const position = playerData?.personal_info?.position || "";
    
    if (position === "Pitcher") {
      return [
        { id: 'pitching-metrics', label: 'Pitching Metrics' },
        { id: 'game-stats', label: 'Game Stats' },
        { id: 'arm-metrics', label: 'Arm Metrics' },
        { id: 'video-analysis', label: 'Video Analysis' }
      ];
    } else if (position.includes("3B") || position === "Third Base") {
      return [
        { id: 'defense-metrics', label: 'Defensive Metrics' },
        { id: 'offensive-stats', label: 'Offensive Stats' },
        { id: 'arm-metrics', label: 'Arm Metrics' },
        { id: 'video-analysis', label: 'Video Analysis' }
      ];
    } else if (position.includes("OF") || position.includes("Outfield")) {
      return [
        { id: 'defense-metrics', label: 'Defensive Metrics' },
        { id: 'offensive-stats', label: 'Offensive Stats' },
        { id: 'speed-metrics', label: 'Speed Metrics' },
        { id: 'video-analysis', label: 'Video Analysis' }
      ];
    } else {
      // Default tabs for other positions
      return [
        { id: 'offensive-stats', label: 'Offensive Stats' },
        { id: 'defense-metrics', label: 'Defensive Metrics' },
        { id: 'athletic-metrics', label: 'Athletic Metrics' },
        { id: 'video-analysis', label: 'Video Analysis' }
      ];
    }
  };

  // Generate sentiment data based on marketability metrics
  const generateSentimentData = () => {
    // Default values if not available
    const positive = playerData?.marketability_metrics?.media_presence?.press_coverage?.positive || 75;
    const neutral = playerData?.marketability_metrics?.media_presence?.press_coverage?.neutral || 20;
    const negative = playerData?.marketability_metrics?.media_presence?.press_coverage?.negative || 5;
    
    return [
      { category: 'Positive', value: positive },
      { category: 'Neutral', value: neutral },
      { category: 'Negative', value: negative }
    ];
  };

  // Generate brand affinity categories
  const generateBrandCategories = () => {
    const compatibleBrands = playerData?.marketability_metrics?.personal_brand?.brand_compatibility || [];
    
    // Default values if not available
    if (compatibleBrands.length === 0) {
      return [
        { category: 'Sports Equipment', score: 85 },
        { category: 'Performance Nutrition', score: 80 },
        { category: 'Athletic Apparel', score: 75 },
        { category: 'Training Tech', score: 70 },
        { category: 'Recovery Products', score: 65 }
      ];
    }
    
    // Create scores for available brand categories
    return compatibleBrands.map((category, index) => {
      // Generate descending scores starting from 90
      const score = 90 - (index * 5);
      return { category, score };
    });
  };
  
  // Colors for sentiment pie chart
  const COLORS = ['#4ade80', '#94a3b8', '#f87171'];

  // Format player name
  const playerName = playerData?.personal_info?.name || "Player Name";
  
  // Position and other basic info
  const position = playerData?.personal_info?.position || "Position";
  const year = playerData?.personal_info?.year || "Year";
  const hometown = playerData?.personal_info?.hometown || "Hometown";
  
  // Physical metrics
  const height = playerData?.athletic_performance?.physical_metrics?.height || "";
  const weight = playerData?.athletic_performance?.physical_metrics?.weight || "";
  const heightWeightDisplay = (height || weight) ? `${height}${weight ? ` / ${weight} lbs` : ""}` : "N/A";
  
  // Get player image
  const playerImage = playerData?.personal_info?.image || "https://via.placeholder.com/400x500";
  
  // SLAM Score
  const slamScore = playerData?.SLAM_Score || 0;
  
  // NIL value
  const nilValue = playerData?.nil_potential_assessment?.projected_annual_nil_value 
    ? `$${playerData?.nil_potential_assessment.projected_annual_nil_value.toLocaleString()}`
    : "$0";
  
  // Social reach
  const socialReach = playerData?.marketability_metrics?.social_media?.total_social_reach || 0;
  const socialGrowth = playerData?.marketability_metrics?.social_media?.social_media_growth_rate || 0;
  
  // Format stats based on position
  const getPitchingStats = () => {
    const currentStats = playerData?.athletic_performance?.career_stats?.pitching?.current_calendar_year || {};
    return {
      era: currentStats.era || 0,
      innings: currentStats.innings_pitched || 0,
      wins: currentStats.wins || 0,
      losses: currentStats.losses || 0,
      strikeouts: currentStats.strikeouts || 0,
      walks: currentStats.walks || 0,
      whip: currentStats.whip || 0
    };
  };
  
  const getBattingStats = () => {
    const currentStats = playerData?.athletic_performance?.career_stats?.batting?.current_calendar_year || {};
    return {
      battingAverage: currentStats.batting_average || 0,
      onBasePercentage: currentStats.on_base_percentage || 0,
      sluggingPercentage: currentStats.slugging_percentage || 0,
      ops: currentStats.ops || 0,
      games: currentStats.games || 0,
      homeRuns: currentStats.home_runs || 0,
      rbis: currentStats.rbis || 0,
      walks: currentStats.walks || 0,
      strikeouts: currentStats.strikeouts || 0
    };
  };

  // Get player awards
  const playerAwards = playerData?.athletic_performance?.awards_recognitions || [];
  
  // Get sentiment data
  const sentimentData = generateSentimentData();
  
  // Get brand categories
  const brandCategories = generateBrandCategories();
  
  // Get key performance metrics
  const keyPerformanceMetrics = playerData?.athletic_performance?.key_performance_metrics || [];

  return (
    <div className="w-full bg-[#0A0A0A] text-[#F5F5F5] rounded-[14px] shadow-lg">
      {/* Header with Sports branding */}
      <div className="bg-[#111111] text-[#F5F5F5] p-4 rounded-t-[14px] flex justify-between items-center shadow-md border-b border-[#1A1A1A]">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/player-roster')}
            className="mr-3 p-1 rounded-full hover:bg-[rgba(255,255,255,0.04)] transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
            <path d="M12 22c-4.2 0-8-3.22-8-8.2 0-4.98 3.8-9 8-9s8 4.02 8 9c0 4.98-3.8 8.2-8 8.2z"></path>
            <path d="M16.5 7.5 12 16l-4.5-8.5"></path>
          </svg>
          <h2 className="text-xl font-bold tracking-tight">Roster Profile: {playerName}</h2>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setVideoModalOpen(true)}
            className="flex items-center bg-[#1A1A1A] text-[#E8B800] hover:bg-[rgba(255,255,255,0.04)] px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
          >
            <Video size={16} className="mr-1.5" />
            Video Analysis
          </button>
          <div className="flex items-center text-sm text-[#E8B800] bg-[#1A1A1A] px-2 py-1 rounded-md">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
              <path d="M2 20l10-10 10 10"></path>
              <path d="M14.5 15l5.5-5.5"></path>
              <path d="M2 10l10-6 10 6"></path>
            </svg>
            SLAM Sports
          </div>
        </div>
      </div>

      {/* Roster Identity Section */}
      <div className="p-4 border-b border-[#1A1A1A]">
        <div className="flex items-center mb-2">
          <User size={20} className="text-[#E8B800] mr-2" />
          <h3 className="text-lg font-bold text-[#F5F5F5]">Roster Profile</h3>
        </div>
        <div className="flex items-start">
          <div className="w-1/4">
            <div className="rounded-lg mb-2 overflow-hidden">
              <img
                src={playerImage}
                alt={playerName}
                className="w-full object-cover"
              />
            </div>
            <div className="text-center text-xs text-[#999999]">SLAM Score: {slamScore}/100</div>
          </div>
          <div className="w-3/4 pl-4">
            <h1 className="text-xl font-bold text-[#F5F5F5]">{playerName}</h1>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div>
                <div className="text-xs text-[#999999]">Position</div>
                <div className="font-medium">{position}</div>
              </div>
              <div>
                <div className="text-xs text-[#999999]">Height/Weight</div>
                <div className="font-medium">{heightWeightDisplay}</div>
              </div>
              <div>
                <div className="text-xs text-[#999999]">Class</div>
                <div className="font-medium">{year}</div>
              </div>
              <div>
                <div className="text-xs text-[#999999]">Hometown</div>
                <div className="font-medium">{hometown || "N/A"}</div>
              </div>
              <div>
                <div className="text-xs text-[#999999]">Team</div>
                <div className="font-medium">{playerData?.personal_info?.current_team || "N/A"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Position-Specific Section */}
      <div className="p-4 border-b border-[#1A1A1A]">
        <div className="flex items-center mb-2">
          <Target size={20} className="text-[#E8B800] mr-2" />
          <h3 className="text-lg font-bold text-[#F5F5F5]">Position-Specific Data: {position}</h3>
        </div>
        
        {/* Position-specific tabs */}
        <div className="flex space-x-2 mb-4 mt-3">
          {getPositionTabs().map(tab => (
            <PositionTab 
              key={tab.id}
              id={tab.id} 
              label={tab.label} 
              active={positionTab === tab.id} 
              onClick={(id) => {
                if (id === 'video-analysis') {
                  setVideoModalOpen(true);
                }
                setPositionTab(id);
              }}
            />
          ))}
        </div>
        
        {/* Pitching Metrics Tab for Pitchers */}
        {position === "Pitcher" && positionTab === 'pitching-metrics' && (
          <div className="grid grid-cols-4 gap-3 mb-2">
            <div className="relative bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
              <div className="text-xs font-medium text-[#999999] border-[#1A1A1A] mb-1 border-b pb-1">ERA</div>
              <div className="text-2xl font-bold text-[#E8B800]">{getPitchingStats().era?.toFixed(2) || "N/A"}</div>
              <div className="text-xs text-[#555555]">Earned Run Average</div>
            </div>
            <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
              <div className="text-xs font-medium text-[#999999] border-[#1A1A1A] mb-1 border-b pb-1">IP</div>
              <div className="text-2xl font-bold text-[#E8B800]">{getPitchingStats().innings?.toFixed(1) || "N/A"}</div>
              <div className="text-xs text-[#555555]">Innings Pitched</div>
            </div>
            <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
              <div className="text-xs font-medium text-[#999999] border-[#1A1A1A] mb-1 border-b pb-1">K</div>
              <div className="text-2xl font-bold text-[#E8B800]">{getPitchingStats().strikeouts || "N/A"}</div>
              <div className="text-xs text-[#555555]">Strikeouts</div>
            </div>
            <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
              <div className="text-xs font-medium text-[#999999] border-[#1A1A1A] mb-1 border-b pb-1">WHIP</div>
              <div className="text-2xl font-bold text-[#E8B800]">{getPitchingStats().whip?.toFixed(2) || "N/A"}</div>
              <div className="text-xs text-[#555555]">Walks & Hits per IP</div>
            </div>
          </div>
        )}
        
        {/* Game Stats Tab for Pitchers */}
        {position === "Pitcher" && positionTab === 'game-stats' && (
          <div className="grid grid-cols-4 gap-3 mb-2">
            <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
              <div className="text-xs font-medium text-[#999999] border-[#1A1A1A] mb-1 border-b pb-1">W</div>
              <div className="text-2xl font-bold text-[#E8B800]">{getPitchingStats().wins || "0"}</div>
              <div className="text-xs text-[#555555]">Wins</div>
            </div>
            <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
              <div className="text-xs font-medium text-[#999999] border-[#1A1A1A] mb-1 border-b pb-1">L</div>
              <div className="text-2xl font-bold text-[#E8B800]">{getPitchingStats().losses || "0"}</div>
              <div className="text-xs text-[#555555]">Losses</div>
            </div>
            <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
              <div className="text-xs font-medium text-[#999999] border-[#1A1A1A] mb-1 border-b pb-1">BB</div>
              <div className="text-2xl font-bold text-[#E8B800]">{getPitchingStats().walks || "0"}</div>
              <div className="text-xs text-[#555555]">Walks</div>
            </div>
            <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
              <div className="text-xs font-medium text-[#999999] border-[#1A1A1A] mb-1 border-b pb-1">K/9</div>
              <div className="text-2xl font-bold text-[#E8B800]">
                {getPitchingStats().innings > 0
                  ? ((getPitchingStats().strikeouts / getPitchingStats().innings) * 9).toFixed(2)
                  : "0.00"}
              </div>
              <div className="text-xs text-[#555555]">Strikeouts per 9 IP</div>
            </div>
          </div>
        )}
        
        {/* Offensive Stats Tab for non-pitchers */}
        {/* {position !== "Pitcher" && positionTab === 'offensive-stats' && (
          <div>
            <div className="text-xs font-medium text-[#F5F5F5] mb-2">{year} Season</div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-[#111111] p-3 rounded-lg shadow-sm border border-[#1A1A1A]">
                <div className="text-xs font-medium text-[#999999] mb-1 border-b border-[#1A1A1A] pb-1">AVG</div>
                <div className="text-2xl font-bold text-[#E8B800]">
                  {getBattingStats().battingAverage?.toFixed(3) || ".000"}
                </div>
                <div className="text-xs text-[#555555]">Batting Average</div>
              </div>
              <div className="bg-[#111111] p-3 rounded-lg shadow-sm border border-[#1A1A1A]">
                <div className="text-xs font-medium text-[#999999] mb-1 border-b border-[#1A1A1A] pb-1">OPS</div>
                <div className="text-2xl font-bold text-[#E8B800]">
                  {getBattingStats().ops?.toFixed(3) || ".000"}
                </div>
                <div className="text-xs text-[#555555]">On-base + Slugging</div>
              </div>
              <div className="bg-[#111111] p-3 rounded-lg shadow-sm border border-[#1A1A1A]">
                <div className="text-xs font-medium text-[#999999] mb-1 border-b border-[#1A1A1A] pb-1">HR</div>
                <div className="text-2xl font-bold text-[#E8B800]">{getBattingStats().homeRuns || "0"}</div>
                <div className="text-xs text-[#555555]">Home Runs</div>
              </div>
            </div>
          </div>
        )} */}
        
        {/* Defensive Metrics Tab */}
        {positionTab === 'defense-metrics' && position !== "Pitcher" && (
          <div className="bg-[#111111] border-[#1A1A1A] text-[#999999] p-3 rounded-lg shadow-sm border mb-3">
            <div className="text-xs font-medium text-[#999999] mb-2">Defensive Summary</div>
            <p className="text-xs text-[#555555]">
              Detailed defensive metrics are not available for this player. Defensive performance is typically measured
              through fielding percentage, assists, putouts, and advanced metrics depending on position.
            </p>
          </div>
        )}
        
        {/* Arm Metrics Tab */}
        {positionTab === 'arm-metrics' && (
          <div className="bg-[#111111] border-[#1A1A1A] text-[#999999] p-3 rounded-lg shadow-sm border mb-3">
            <div className="text-xs font-medium text-[#999999] mb-2">Arm Metrics Summary</div>
            <p className="text-xs text-[#555555]">
              {position === "Pitcher"
                ? `${playerName} is known for ${playerData?.athletic_performance?.playing_style || "effective pitching"}. Detailed arm metrics include velocity, command, and pitch movement data.`
                : `Arm metrics include throw velocity, accuracy, and exchange time. Detailed measurements are not available in this dataset.`
              }
            </p>
          </div>
        )}
      </div>

      {/* Performance Highlights */}
      <div className="p-4 border-b border-[#1A1A1A]">
        <div className="flex items-center mb-3">
          <Award size={20} className="text-[#E8B800] mr-2" />
          <h3 className="text-lg font-bold text-[#F5F5F5]">Performance Analysis</h3>
        </div>

        <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border mb-3">
          <div className="text-xs font-medium text-[#999999] mb-2">Key Performance Metrics</div>
          <ul className="text-xs text-[#555555]">
            {keyPerformanceMetrics.length > 0 ? (
              keyPerformanceMetrics.map((metric, index) => (
                <li key={index} className="mb-1 flex items-start">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#E8B800] mr-2 mt-1"></span>
                  {metric}
                </li>
              ))
            ) : (
              <li className="mb-1">No detailed performance metrics available</li>
            )}
          </ul>
        </div>

        <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border mb-3">
          <div className="text-xs font-medium text-[#999999] mb-2">Awards & Recognition</div>
          <ul className="text-xs text-[#555555]">
            {playerAwards.length > 0 ? (
              playerAwards.slice(0, 5).map((award, index) => (
                <li key={index} className="mb-1 flex items-start">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#E8B800] mr-2 mt-1"></span>
                  {award}
                </li>
              ))
            ) : (
              <li className="mb-1">No awards or recognitions available</li>
            )}
            {playerAwards.length > 5 && (
              <li className="text-[#E8B800] text-xs cursor-pointer hover:underline">
                +{playerAwards.length - 5} more awards
              </li>
            )}
          </ul>
        </div>
      </div>
      
      {/* NIL Analytics Section */}
      <div className="p-4 border-b border-[#1A1A1A]">
        <div className="flex items-center mb-3">
          <TrendingUp size={20} className="text-[#E8B800] mr-2" />
          <h3 className="text-lg font-bold text-[#F5F5F5]">NIL Analytics</h3>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-3">
          <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
            <div className="text-xs font-medium text-[#E8B800] border-[#1A1A1A] mb-1 border-b pb-1">Marketability Score</div>
            <div className="text-2xl font-bold text-[#E8B800]">{slamScore}/100</div>
            <div className="text-xs text-[#999999] mt-1">
              {slamScore > 75 ? "Top 25%" : slamScore > 50 ? "Top 50%" : "Average"} in NCAA {playerData?.personal_info?.sport || "Sports"}
            </div>
          </div>

          <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
            <div className="text-xs font-medium text-[#E8B800] border-[#1A1A1A] mb-1 border-b pb-1">Social Audience</div>
            <div className="text-2xl font-bold text-[#E8B800]">
              {socialReach > 1000
                ? `${(socialReach/1000).toFixed(1)}K`
                : socialReach || "0"}
            </div>
            <div className="text-xs text-green-400 mt-1">
              {socialGrowth > 0 ? `+${socialGrowth}% growth` : "No growth data"}
            </div>
          </div>

          <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg shadow-sm border">
            <div className="text-xs font-medium text-[#E8B800] border-[#1A1A1A] mb-1 border-b pb-1">Est. NIL Value</div>
            <div className="text-2xl font-bold text-[#E8B800]">{nilValue}</div>
            <div className="text-xs text-[#999999] mt-1">
              {playerData?.nil_potential_assessment?.projected_annual_nil_value ? "Per endorsement deal" : "Potential value"}
            </div>
          </div>
        </div>
        
        <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg border mb-3">
          <div className="text-xs font-medium text-[#999999] mb-2">Top Brand Affinities</div>
          <div className="grid grid-cols-5 gap-2">
            {brandCategories.slice(0, 5).map((brand, index) => (
              <div key={index} className="border border-[#1A1A1A] bg-[#0A0A0A] rounded p-2 text-center">
                <div className="text-xs font-medium text-[#999999]">{brand.category}</div>
                <div className="text-sm font-bold text-[#E8B800] mt-1">{brand.score}%</div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-[#111111] border-[#1A1A1A] p-3 rounded-lg border">
          <div className="text-xs font-medium text-[#E8B800] mb-2">NIL Opportunity Summary</div>
          <p className="text-xs text-[#999999] mb-2">
            {playerData?.nil_potential_assessment?.growth_potential?.brand_development_opportunities?.[0] ||
              `${playerName} has NIL potential based on performance metrics and market position.
              ${playerData?.marketability_metrics?.personal_brand?.personal_narrative || ""}`
            }
          </p>
          <div className="flex justify-between items-center text-xs text-[#555555]">
            <span>
              {playerData?.marketability_metrics?.social_media?.platforms?.[0] ?
                `Social Media: ${playerData?.marketability_metrics.social_media.platforms[0]}` :
                "Social Media: Limited presence"}
            </span>
            <span>
              {playerData?.marketability_metrics?.social_media?.social_media_valuation ?
                `Est. Value: $${playerData?.marketability_metrics.social_media.social_media_valuation}` :
                "Growth Potential: Significant"}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 bg-[#111111] border-t border-[#1A1A1A] rounded-b-[14px]">
        <div className="flex justify-between items-center">
          <div className="text-xs text-[#555555] flex items-center">
            <span className="inline-block w-2 h-2 rounded-full bg-[#E8B800] mr-1 animate-pulse"></span>
            <span>Last updated: May 9, 2025</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setVideoModalOpen(true)}
              className="bg-[#E8B800] text-black px-2 py-1 rounded-lg text-xs font-medium hover:bg-[#d4a800] transition-colors flex items-center"
            >
              <Video size={12} className="mr-1" />
              Video Analysis
            </button>
            <button className="bg-[#222222] text-[#F5F5F5] px-2 py-1 rounded-lg text-xs font-medium hover:bg-[rgba(255,255,255,0.04)] transition-colors">
              Full Report
            </button>
          </div>
        </div>
      </div>

      {/* Video Analytics Modal would be implemented here */}
      <PlayerChatbot playerId={playerId}  name={playerData?.personal_info?.name}/>
    </div>
  );
};

export default PlayerProfile;